// src/use-echarts.ts
import { useEffect, useRef, useState } from "react";

// src/utils/chart-init.ts
import { init, use as echartsUse } from "echarts/core";
async function getGlobalUse() {
  const modules = [
    import("echarts/features"),
    import("echarts/charts"),
    import("echarts/components"),
    import("echarts/renderers")
  ];
  const loadedModules = await Promise.all(
    modules.map((m) => m.then((m2) => Object.values(m2)))
  );
  return loadedModules.flat();
}
function initializeECharts(container, options) {
  const {
    theme,
    devicePixelRatio,
    height,
    locale,
    pointerSize,
    renderer,
    useCoarsePointer,
    useDirtyRect,
    width
  } = options;
  return init(container, theme, {
    devicePixelRatio,
    height,
    locale,
    pointerSize,
    renderer,
    useCoarsePointer,
    useDirtyRect,
    width
  });
}
async function setupECharts(container, options) {
  if (!container) return;
  const useOpts = options.use || await getGlobalUse();
  echartsUse(useOpts);
  return initializeECharts(container, options);
}

// src/utils/chart-options.ts
var conditionalProps = [
  // Animation properties
  "animation",
  "animationDelay",
  "animationDelayUpdate",
  "animationDuration",
  "animationDurationUpdate",
  "animationEasing",
  "animationEasingUpdate",
  "animationThreshold",
  // Axis properties
  "angleAxis",
  "axisPointer",
  "parallelAxis",
  "radiusAxis",
  "singleAxis",
  // Visual properties
  "backgroundColor",
  "blendMode",
  "color",
  "darkMode",
  "textStyle",
  "visualMap",
  // Chart components
  "aria",
  "brush",
  "calendar",
  "dataset",
  "dataZoom",
  "geo",
  "graphic",
  "grid",
  "legend",
  "media",
  "options",
  "parallel",
  "polar",
  "radar",
  "stateAnimation",
  "timeline",
  "title",
  "toolbox",
  "tooltip"
];
var buildChartOptions = (props) => {
  const baseOptions = {
    series: props.series,
    useUTC: props.useUTC,
    xAxis: props.xAxis,
    yAxis: props.yAxis,
    progressive: props.progressive,
    blendMode: props.blendMode,
    hoverLayerThreshold: props.hoverLayerThreshold,
    progressiveThreshold: props.progressiveThreshold
  };
  for (const prop of conditionalProps) {
    if (props[prop]) {
      ;
      baseOptions[prop] = props[prop];
    }
  }
  return baseOptions;
};
var getSetOptionConfig = (props) => {
  return {
    lazyUpdate: props.lazyUpdate,
    notMerge: props.notMerge,
    replaceMerge: props.replaceMerge,
    silent: props.silent,
    transition: props.transition
  };
};

// src/utils/event-handlers.ts
var eventMap = {
  axisareaselected: "onAxisAreaSelected",
  brush: "onBrush",
  brushend: "onBrushEnd",
  brushselected: "onBrushSelected",
  click: "onClick",
  contextmenu: "onContextMenu",
  datarangeselected: "onDataRangeSelected",
  dataviewchanged: "onDataViewChanged",
  datazoom: "onDataZoom",
  dblclick: "onDoubleClick",
  downplay: "onDownplay",
  finished: "onFinished",
  geoselectchanged: "onGeoSelectChanged",
  geoselected: "onGeoSelected",
  geounselected: "onGeoUnselected",
  globalcursortaken: "onGlobalCursorTaken",
  globalout: "onGlobalOut",
  highlight: "onHighlight",
  legendinverseselect: "onLegendInverseSelect",
  legendscroll: "onLegendScroll",
  legendselectchanged: "onLegendSelectChanged",
  legendselected: "onLegendSelected",
  legendunselected: "onLegendUnselected",
  magictypechanged: "onMagicTypeChanged",
  mousedown: "onMouseDown",
  mousemove: "onMouseMove",
  mouseout: "onMouseOut",
  mouseover: "onMouseOver",
  rendered: "onRendered",
  restore: "onRestore",
  selectchanged: "onSelectChanged",
  timelinechanged: "onTimelineChanged",
  timelineplaychanged: "onTimelinePlayChanged"
};
function clearEventListeners(instance) {
  for (const eventName of Object.keys(eventMap)) {
    instance.off(eventName);
  }
}
function setupEventHandlers(instance, options) {
  clearEventListeners(instance);
  for (const [eventName, propName] of Object.entries(eventMap)) {
    const handler = options[propName];
    if (typeof handler === "function") {
      instance.on(eventName, handler);
    }
  }
}

// src/use-echarts.ts
function useECharts(options) {
  const containerRef = useRef();
  const echartsRef = useRef();
  const resizeObserverRef = useRef();
  const [started, setStarted] = useState(false);
  const echartsInstance = echartsRef.current;
  const setContainerRef = async (node) => {
    if (!node || node === containerRef.current) return;
    if (echartsRef.current) echartsRef.current.dispose();
    containerRef.current = node;
    echartsRef.current = await setupECharts(node, options);
    resizeObserverRef.current = startResizeObserver();
    setStarted(true);
  };
  const startResizeObserver = () => {
    const resizeObserver = new ResizeObserver(() => {
      echartsRef.current?.resize();
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return resizeObserver;
  };
  useEffect(() => {
    return () => echartsInstance?.dispose?.();
  }, [echartsInstance]);
  useEffect(() => {
    if (!echartsInstance || !started) return;
    if (options.group) echartsInstance.group = options.group;
  }, [options.group, started, echartsInstance]);
  useEffect(() => {
    if (!echartsInstance || !started) return;
    const chartOptions = buildChartOptions(options);
    const setOptionConfig = getSetOptionConfig(options);
    echartsInstance.setOption(chartOptions, setOptionConfig);
  }, [
    options.angleAxis,
    options.animation,
    options.animationDelay,
    options.animationDelayUpdate,
    options.animationDuration,
    options.animationDurationUpdate,
    options.animationEasing,
    options.animationEasingUpdate,
    options.animationThreshold,
    options.aria,
    options.axisPointer,
    options.backgroundColor,
    options.blendMode,
    options.brush,
    options.calendar,
    options.color,
    options.darkMode,
    options.dataset,
    options.dataZoom,
    options.geo,
    options.graphic,
    options.grid,
    options.hoverLayerThreshold,
    options.legend,
    options.media,
    options.options,
    options.parallel,
    options.parallelAxis,
    options.polar,
    options.progressive,
    options.progressiveThreshold,
    options.radar,
    options.radiusAxis,
    options.series,
    options.singleAxis,
    options.stateAnimation,
    options.textStyle,
    options.timeline,
    options.title,
    options.toolbox,
    options.tooltip,
    options.useUTC,
    options.visualMap,
    options.xAxis,
    options.yAxis,
    //
    options.lazyUpdate,
    options.notMerge,
    options.replaceMerge,
    options.silent,
    options.transition,
    //
    started,
    echartsInstance
  ]);
  useEffect(() => {
    if (!echartsInstance || !started) return;
    setupEventHandlers(echartsInstance, options);
  }, [
    started,
    echartsInstance,
    options.onAxisAreaSelected,
    options.onBrush,
    options.onBrushEnd,
    options.onBrushSelected,
    options.onClick,
    options.onContextMenu,
    options.onDataRangeSelected,
    options.onDataViewChanged,
    options.onDataZoom,
    options.onDoubleClick,
    options.onDownplay,
    options.onFinished,
    options.onGeoSelectChanged,
    options.onGeoSelected,
    options.onGeoUnselected,
    options.onGlobalCursorTaken,
    options.onGlobalOut,
    options.onHighlight,
    options.onLegendInverseSelect,
    options.onLegendScroll,
    options.onLegendSelectChanged,
    options.onLegendSelected,
    options.onLegendUnselected,
    options.onMagicTypeChanged,
    options.onMouseDown,
    options.onMouseMove,
    options.onMouseOut,
    options.onMouseOver,
    options.onRendered,
    options.onRestore,
    options.onSelectChanged,
    options.onTimelineChanged,
    options.onTimelinePlayChanged
  ]);
  return [setContainerRef, echartsRef.current];
}

// src/use-connect.ts
import { connect, disconnect } from "echarts/core";
import { useState as useState2 } from "react";
var useConnect = (group) => {
  const [isConnected, setIsConnected] = useState2(false);
  function onChange(connected) {
    if (connected) {
      connect(group);
    } else {
      disconnect(group);
    }
    setIsConnected(connected);
  }
  return [isConnected, onChange];
};

// src/echart.tsx
import { jsx } from "react/jsx-runtime";
var EChart = ({
  // Initialization options
  devicePixelRatio,
  height,
  locale,
  pointerSize,
  renderer,
  theme,
  use,
  useCoarsePointer,
  useDirtyRect,
  width,
  // ECharts instance options
  group,
  // SetOption options
  lazyUpdate,
  notMerge,
  replaceMerge,
  silent,
  transition,
  darkMode,
  media,
  options,
  stateAnimation,
  // Chart options
  angleAxis,
  animation,
  animationDelay,
  animationDelayUpdate,
  animationDuration,
  animationDurationUpdate,
  animationEasing,
  animationEasingUpdate,
  animationThreshold,
  aria,
  axisPointer,
  backgroundColor,
  blendMode,
  brush,
  calendar,
  color,
  dataZoom,
  dataset,
  geo,
  graphic,
  grid,
  hoverLayerThreshold,
  legend,
  parallel,
  parallelAxis,
  polar,
  progressive,
  progressiveThreshold,
  radar,
  radiusAxis,
  series,
  singleAxis,
  textStyle,
  timeline,
  title,
  toolbox,
  tooltip,
  useUTC,
  visualMap,
  xAxis,
  yAxis,
  // Event handlers
  onAxisAreaSelected,
  onBrush,
  onBrushEnd,
  onBrushSelected,
  onClick,
  onContextMenu,
  onDataRangeSelected,
  onDataViewChanged,
  onDataZoom,
  onDoubleClick,
  onDownplay,
  onFinished,
  onGeoSelectChanged,
  onGeoSelected,
  onGeoUnselected,
  onGlobalCursorTaken,
  onGlobalOut,
  onHighlight,
  onLegendInverseSelect,
  onLegendScroll,
  onLegendSelectChanged,
  onLegendSelected,
  onLegendUnselected,
  onMagicTypeChanged,
  onMouseDown,
  onMouseMove,
  onMouseOut,
  onMouseOver,
  onRendered,
  onRestore,
  onSelectChanged,
  onTimelineChanged,
  onTimelinePlayChanged,
  ...rest
}) => {
  const [ref] = useECharts({
    // Initialization options
    devicePixelRatio,
    height,
    locale,
    pointerSize,
    renderer,
    theme,
    use,
    useCoarsePointer,
    useDirtyRect,
    width,
    // ECharts instance options
    group,
    // SetOption options
    lazyUpdate,
    notMerge,
    replaceMerge,
    silent,
    transition,
    darkMode,
    media,
    options,
    stateAnimation,
    // Chart options
    angleAxis,
    animation,
    animationDelay,
    animationDelayUpdate,
    animationDuration,
    animationDurationUpdate,
    animationEasing,
    animationEasingUpdate,
    animationThreshold,
    aria,
    axisPointer,
    backgroundColor,
    blendMode,
    brush,
    calendar,
    color,
    dataZoom,
    dataset,
    geo,
    graphic,
    grid,
    hoverLayerThreshold,
    legend,
    parallel,
    parallelAxis,
    polar,
    progressive,
    progressiveThreshold,
    radar,
    radiusAxis,
    series,
    singleAxis,
    textStyle,
    timeline,
    title,
    toolbox,
    tooltip,
    useUTC,
    visualMap,
    xAxis,
    yAxis,
    // Event handlers
    onAxisAreaSelected,
    onBrush,
    onBrushEnd,
    onBrushSelected,
    onClick,
    onContextMenu,
    onDataRangeSelected,
    onDataViewChanged,
    onDataZoom,
    onDoubleClick,
    onDownplay,
    onFinished,
    onGeoSelectChanged,
    onGeoSelected,
    onGeoUnselected,
    onGlobalCursorTaken,
    onGlobalOut,
    onHighlight,
    onLegendInverseSelect,
    onLegendScroll,
    onLegendSelectChanged,
    onLegendSelected,
    onLegendUnselected,
    onMagicTypeChanged,
    onMouseDown,
    onMouseMove,
    onMouseOut,
    onMouseOver,
    onRendered,
    onRestore,
    onSelectChanged,
    onTimelineChanged,
    onTimelinePlayChanged
  });
  return /* @__PURE__ */ jsx("div", { ...rest, ref });
};
export {
  EChart,
  useConnect,
  useECharts
};
//# sourceMappingURL=index.mjs.map